import { FormField, SelectField, TextareaField } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { useESGReportBuilderStore } from "@/features/esg/hooks/useESGReportStore";
import {
  formatYear,
  generateYearOptions,
} from "@/features/esg/utils/year-helpers";
import { useTran } from "@/hooks";
import { observer } from "mobx-react-lite";
import { useMemo } from "react";

const ReportSettingsStep = observer(({ frameworkInfo }) => {
  const { t } = useTran();
  const {
    reportYear,
    setReportYear,
    reportName,
    setReportName,
    notes,
    setNotes,
    includeToc,
    includeCover,
    setIncludeToc,
    setIncludeCover,
  } = useESGReportBuilderStore();

  const frameworkPoints = useMemo(
    () => frameworkInfo?.points || [],
    [frameworkInfo],
  );

  const yearOptions = generateYearOptions().map((year) => ({
    value: String(year),
    label: formatYear(year),
  }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
      <div className="md:col-span-2 rounded-radius bg-card shadow-sm border-border border p-4 space-y-2">
        <div className="flex items-center justify-center p-2">
          <img
            src={frameworkInfo.image}
            alt={`${frameworkInfo?.value || "Framework"} logo`}
            className="h-[100px] w-[100px] object-contain"
          />
        </div>
        <p className="text-sm text-muted-foreground">
          {frameworkInfo?.summary}
        </p>
        <p className="text-sm text-muted-foreground">{frameworkInfo?.usage}</p>
        {frameworkPoints?.length > 0 ? (
          <ul className="ml-4 list-disc space-y-2 text-sm text-muted-foreground">
            {frameworkPoints.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="md:col-span-3 rounded-radius bg-card shadow-sm border-border border p-4 space-y-4">
        <h3 className="font-semibold mb-5 text-card-foreground">
          {frameworkInfo.value}
        </h3>
        <SelectField
          options={yearOptions}
          label={t("Reporting Year")}
          value={reportYear}
          onValueChange={setReportYear}
          required
        />
        <FormField
          label={t("Report Name")}
          value={reportName}
          onChange={(e) => setReportName(e.target.value)}
          required
        />
        <TextareaField
          label={t("Notes / Comments")}
          placeholder={t("Internal notes for this report")}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="rounded-md border-border border p-3 flex items-center justify-between">
            <p className="text-sm text-foreground">{t("Include cover page")}</p>
            <Switch
              id="switch-cover"
              checked={includeCover}
              onCheckedChange={setIncludeCover}
            />
          </div>
          <div className="rounded-md border-border border p-2 flex items-center justify-between">
            <p className="text-sm text-foreground">
              {t("Include table of contents")}
            </p>
            <Switch
              id="switch-toc"
              checked={includeToc}
              onCheckedChange={setIncludeToc}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReportSettingsStep;
